import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import Swal from 'sweetalert2';
import { apiUrl } from '../../config/api';
import { saveAuth } from '../../hooks/useAuthStorage';
import logo from '../../images/amazing gems and jewellery png with name.png';
import './LoginModal.scss';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const INITIAL_FORM = { name: '', email: '', contactNo: '' };

/**
 * Guest checkout registration with e-mail OTP.
 *
 * Shares the same fix as SignupModal: the step is React state rather
 * than `document.querySelector(...).style.display`, which previously
 * collided with the identically-classed elements in SignupModal.
 */
const GuestLoginModal = ({ show, onClose }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState('details'); // details -> otp -> verified
  const [otp, setOtp] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!show) {
      setForm(INITIAL_FORM);
      setErrors({});
      setStep('details');
      setOtp('');
      setIsSubmitting(false);
    }
  }, [show]);

  const setField = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Please enter your name.';
    if (!EMAIL_PATTERN.test(form.email.trim())) next.email = 'Please enter a valid email address.';
    if (!form.contactNo) next.contactNo = 'Please enter a contact number.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSendOtp = async () => {
    if (!validate() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(apiUrl('/api/send-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email.trim() }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        Swal.fire({
          icon: 'warning',
          confirmButtonColor: '#a17e33',
          title: 'Could not send the code',
          text: data.message || 'Please check the email address and try again.',
        });
        return;
      }

      setStep('otp');
      setOtp('');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        confirmButtonColor: '#a17e33',
        title: 'Network error',
        text: 'We could not reach the server. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    if (!otp.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(apiUrl('/api/verify-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email.trim(), otp: otp.trim() }),
      });

      if (!response.ok) {
        Swal.fire({
          icon: 'warning',
          confirmButtonColor: '#a17e33',
          title: 'That code did not match',
          text: 'Please check the code, or request a new one.',
        });
        setOtp('');
        return;
      }

      setStep('verified');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        confirmButtonColor: '#a17e33',
        title: 'Network error',
        text: 'We could not verify the code. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGuestLogin = async (event) => {
    event.preventDefault();
    if (step !== 'verified' || isSubmitting) return;

    setIsSubmitting(true);
    const common_id = localStorage.getItem('common_id') || '';

    try {
      const response = await fetch(apiUrl('/api/guest-register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          common_id,
          name: form.name.trim(),
          email: form.email.trim(),
          contact_no: form.contactNo,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (data.token && data.user_id) {
        saveAuth({
          token: data.token,
          user_id: data.user_id,
          user_name: data.user_name,
        });

        onClose();
        Swal.fire({
          icon: 'success',
          confirmButtonColor: '#a17e33',
          title: 'Signed in as guest',
        });
      } else {
        Swal.fire({
          icon: 'warning',
          confirmButtonColor: '#a17e33',
          title: 'Guest login failed',
          text: data.message || 'Please try again.',
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        confirmButtonColor: '#a17e33',
        title: 'Network error',
        text: 'We could not sign you in. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldsDisabled = step !== 'details' || isSubmitting;

  return (
    <Modal show={show} onHide={onClose} centered scrollable>
      <Modal.Header closeButton>
        <span className="auth-modal__brand-label">Guest checkout</span>
      </Modal.Header>
      <Modal.Body>
        <div className="auth-modal">
          <img className="auth-modal__logo" src={logo} alt="Bansal Attire" />
          <h2 className="auth-modal__title">Continue as Guest</h2>

          <form className="auth-form" onSubmit={handleGuestLogin} noValidate>
            <div className="field">
              <label className="field__label" htmlFor="guest-name">
                Full name
              </label>
              <input
                id="guest-name"
                className="input"
                type="text"
                value={form.name}
                onChange={(event) => setField('name')(event.target.value)}
                aria-invalid={Boolean(errors.name)}
                disabled={fieldsDisabled}
                autoComplete="name"
              />
              {errors.name && <span className="field__error">{errors.name}</span>}
            </div>

            <div className="field">
              <label className="field__label" htmlFor="guest-email">
                Email
              </label>
              <input
                id="guest-email"
                className="input"
                type="email"
                value={form.email}
                onChange={(event) => setField('email')(event.target.value)}
                aria-invalid={Boolean(errors.email)}
                disabled={fieldsDisabled}
                autoComplete="email"
              />
              {errors.email && <span className="field__error">{errors.email}</span>}
            </div>

            <div className="field">
              <label className="field__label" htmlFor="guest-phone">
                Contact number
              </label>
              <PhoneInput
                id="guest-phone"
                placeholder="Enter phone number"
                value={form.contactNo}
                onChange={(value) => setField('contactNo')(value ?? '')}
                className="phone-input"
                disabled={fieldsDisabled}
              />
              {errors.contactNo && <span className="field__error">{errors.contactNo}</span>}
            </div>

            {step === 'details' && (
              <button
                type="button"
                className="btn btn--primary btn--block"
                onClick={handleSendOtp}
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="btn__spinner" aria-hidden="true" />
                    Sending code…
                  </>
                ) : (
                  'Verify email'
                )}
              </button>
            )}

            {step === 'verified' && (
              <>
                <p className="alert alert--success" role="status">
                  Email verified — continue to checkout.
                </p>
                <button
                  type="submit"
                  className="btn btn--primary btn--block"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="btn__spinner" aria-hidden="true" />
                      Signing in…
                    </>
                  ) : (
                    'Continue'
                  )}
                </button>
              </>
            )}
          </form>

          {step === 'otp' && (
            <form className="auth-form auth-form--otp" onSubmit={handleVerifyOtp}>
              <p className="field__hint">
                We sent a code to <strong>{form.email}</strong>.
              </p>
              <div className="field">
                <label className="field__label" htmlFor="guest-otp">
                  Verification code
                </label>
                <input
                  id="guest-otp"
                  className="input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={(event) => setOtp(event.target.value)}
                  disabled={isSubmitting}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn--primary btn--block"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="btn__spinner" aria-hidden="true" />
                    Verifying…
                  </>
                ) : (
                  'Verify code'
                )}
              </button>
              <button
                type="button"
                className="btn btn--link btn--block"
                onClick={handleSendOtp}
                disabled={isSubmitting}
              >
                Resend code
              </button>
            </form>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default GuestLoginModal;
